import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime, fmtNum, fmtChange } from "@/lib/format";
import { TrendingUp, TrendingDown, Info } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Bar, Cell, ReferenceLine } from "recharts";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import LastUpdateChip from "@/components/LastUpdateChip";
import {
  Tooltip as UiTooltip,
  TooltipContent as UiTooltipContent,
  TooltipProvider as UiTooltipProvider,
  TooltipTrigger as UiTooltipTrigger,
} from "@/components/ui/tooltip";

const CHANGE_TOOLTIP_TEXT = "Differenza tra il prezzo di oggi (Ask o Last, quando disponibile) e la chiusura del giorno di trading precedente (Close).";

export default function ForwardPage({ sheet, title }) {
  const PREFS_KEY = `adybo-forward-chart-prefs-${sheet}`;
  const loadPrefs = () => {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      if (!raw) return {};
      const p = JSON.parse(raw);
      return typeof p === "object" && p !== null ? p : {};
    } catch { return {}; }
  };
  const initialPrefs = loadPrefs();

  const [prices, setPrices] = useState([]);
  const [compare, setCompare] = useState({ current: [], historical: [], historical_date: null, current_date: null });
  const [contractHistory, setContractHistory] = useState({ contract_name: null, points: [], first_date: null, last_date: null });
  const [overlayHistory, setOverlayHistory] = useState({ contract_name: null, points: [] });
  const [chartMode, setChartMode] = useState(initialPrefs.chartMode || "preset"); // "preset" | "range"
  const [chartDays, setChartDays] = useState(initialPrefs.chartDays || 30);
  const [chartDateFrom, setChartDateFrom] = useState(initialPrefs.chartDateFrom || "");
  const [chartDateTo, setChartDateTo] = useState(initialPrefs.chartDateTo || "");
  const [chartContract, setChartContract] = useState(initialPrefs.chartContract || ""); // "" = auto CAL N+1
  const [chartOverlay, setChartOverlay] = useState(initialPrefs.chartOverlay !== false);
  const [periodType, setPeriodType] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [unitGas, setUnitGas] = useState("mwh");
  const [sortKey, setSortKey] = useState("contract_name");
  const [sortDir, setSortDir] = useState("asc");
  const SMC_FACTOR = 1.05833;
  const isGas = sheet === "TTF" || sheet === "PSV";
  const isCO2 = sheet === "CO2";
  const useSmc = isGas && unitGas === "smc";
  const conv = (v) => (v == null ? null : (useSmc ? v * SMC_FACTOR : v));
  const unitLabel = isCO2 ? "€/tCO2" : (useSmc ? "c€/Smc" : "€/MWh");

  const load = useCallback(async (opts = {}) => {
    if (opts.silent) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await api.get("/prices", { params: { source_sheet: sheet } });
      setPrices(data);
      try {
        const cmp = await api.get("/prices/compare", { params: { source_sheet: sheet, days_ago: 7 } });
        setCompare(cmp.data);
      } catch { /* compare optional */ }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [sheet]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh silenzioso 60s + refresh immediato al ritorno sul tab
  useAutoRefresh(() => load({ silent: true }), 60_000);

  // Contratti disponibili nell'ultimo upload (per il dropdown)
  const currentYear = new Date().getFullYear();
  const forwardPeriodType = isCO2 ? "MidDec" : "Calendar";
  const calContracts = useMemo(() => {
    const list = prices
      .filter((p) => p.period_type === forwardPeriodType && p.period_year != null)
      .map((p) => ({ name: p.contract_name, year: Number(p.period_year) }))
      .filter((c) => c.year > currentYear)
      .sort((a, b) => a.year - b.year);
    // dedupe by name
    const seen = new Set();
    return list.filter((c) => { if (seen.has(c.name)) return false; seen.add(c.name); return true; });
  }, [prices, forwardPeriodType, currentYear]);
  const monthContracts = useMemo(() => {
    if (isCO2) return [];
    const MONTH_MAP = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
    const parseMc = (name) => {
      const m = /^([A-Za-z]{3})-(\d{4})$/.exec(name || "");
      if (!m) return null;
      const mi = MONTH_MAP[m[1]];
      if (mi == null) return null;
      return new Date(Number(m[2]), mi, 1).getTime();
    };
    const now = Date.now();
    const list = prices
      .filter((p) => p.period_type === "Month" && p.contract_name)
      .map((p) => ({ name: p.contract_name, t: parseMc(p.contract_name) }))
      .filter((c) => c.t != null && c.t >= now - 32 * 24 * 3600 * 1000)
      .sort((a, b) => a.t - b.t);
    const seen = new Set();
    return list.filter((c) => { if (seen.has(c.name)) return false; seen.add(c.name); return true; });
  }, [prices, isCO2]);

  const quarterContracts = useMemo(() => {
    if (isCO2) return [];
    // Q1-YYYY / Q2-YYYY etc. → data inizio trimestre
    const parseQ = (name) => {
      const m = /^Q([1-4])-(\d{4})$/i.exec(name || "");
      if (!m) return null;
      return new Date(Number(m[2]), (Number(m[1]) - 1) * 3, 1).getTime();
    };
    const now = Date.now();
    const list = prices
      .filter((p) => p.period_type === "Quarter" && p.contract_name)
      .map((p) => ({ name: p.contract_name, t: parseQ(p.contract_name) }))
      .filter((c) => c.t != null && c.t >= now - 92 * 24 * 3600 * 1000)
      .sort((a, b) => a.t - b.t);
    const seen = new Set();
    return list.filter((c) => { if (seen.has(c.name)) return false; seen.add(c.name); return true; });
  }, [prices, isCO2]);

  const seasonContracts = useMemo(() => {
    if (isCO2) return [];
    // Formati accettati: Sum-YYYY / Win-YYYY / Sum-YY / Win-YY(/YY)?
    const parseS = (name) => {
      let m = /^Sum-(\d{4})$/i.exec(name || "");
      if (m) return { t: new Date(Number(m[1]), 3, 1).getTime(), sort: Number(m[1]) + 0.4 };
      m = /^Win-(\d{4})$/i.exec(name || "");
      if (m) return { t: new Date(Number(m[1]), 9, 1).getTime(), sort: Number(m[1]) + 0.9 };
      m = /^Sum-(\d{2})$/i.exec(name || "");
      if (m) return { t: new Date(2000 + Number(m[1]), 3, 1).getTime(), sort: 2000 + Number(m[1]) + 0.4 };
      m = /^Win-(\d{2})(?:\/\d{2})?$/i.exec(name || "");
      if (m) return { t: new Date(2000 + Number(m[1]), 9, 1).getTime(), sort: 2000 + Number(m[1]) + 0.9 };
      return null;
    };
    const now = Date.now();
    const list = prices
      .filter((p) => p.period_type === "Season" && p.contract_name)
      .map((p) => {
        const k = parseS(p.contract_name);
        return { name: p.contract_name, t: k?.t, sort: k?.sort };
      })
      .filter((c) => c.t != null && c.t >= now - 183 * 24 * 3600 * 1000)
      .sort((a, b) => a.sort - b.sort);
    const seen = new Set();
    return list.filter((c) => { if (seen.has(c.name)) return false; seen.add(c.name); return true; });
  }, [prices, isCO2]);

  const primaryContractName = chartContract || (calContracts[0]?.name || null);
  // Nome + label del contratto overlay (N+1 stesso periodo, anno successivo).
  // Funziona per Calendar/MidDec (successivo nell'elenco), Quarter (Q1-2027 → Q1-2028),
  // Season (Sum-2027 → Sum-2028), Month (Oct-2026 → Oct-2027).
  const overlayInfo = useMemo(() => {
    if (!chartOverlay) return { name: null, label: null };
    const n = primaryContractName || "";
    if (!n) return { name: null, label: null };
    // Calendar / MidDec
    if (/^(Cal|MidDec)-\d{4}$/i.test(n)) {
      const idx = calContracts.findIndex((c) => c.name === n);
      if (idx >= 0 && idx + 1 < calContracts.length) return { name: calContracts[idx + 1].name, label: `${/^MidDec/i.test(n) ? "MidDec" : "CAL"} N+2` };
      return { name: null, label: null };
    }
    // Quarter: Q[1-4]-YYYY → stesso Q, +1 anno
    let m = /^(Q[1-4])-(\d{4})$/i.exec(n);
    if (m) {
      const next = `${m[1]}-${Number(m[2]) + 1}`;
      // verifica che esista effettivamente
      if (quarterContracts.some((c) => c.name === next)) return { name: next, label: "Quarter anno succ." };
      return { name: null, label: null };
    }
    // Season Sum/Win-YYYY (4 cifre)
    m = /^(Sum|Win)-(\d{4})$/i.exec(n);
    if (m) {
      const next = `${m[1]}-${Number(m[2]) + 1}`;
      if (seasonContracts.some((c) => c.name === next)) return { name: next, label: "Season anno succ." };
      return { name: null, label: null };
    }
    // Season legacy: Sum-YY / Win-YY(/YY)?
    m = /^(Sum|Win)-(\d{2})(?:\/\d{2})?$/i.exec(n);
    if (m) {
      const nextYY = String((Number(m[2]) + 1) % 100).padStart(2, "0");
      // Prova prima Win-YY/YY+1 (per Win che spesso è "Win-25/26"), poi Sum/Win-YY
      const cand1 = `${m[1]}-${nextYY}`;
      const cand2 = m[1].toLowerCase() === "win" ? `Win-${nextYY}/${String((Number(m[2]) + 2) % 100).padStart(2, "0")}` : null;
      const found = [cand1, cand2].filter(Boolean).find((c) => seasonContracts.some((s) => s.name === c));
      if (found) return { name: found, label: "Season anno succ." };
      return { name: null, label: null };
    }
    // Month: MMM-YYYY → MMM-(YYYY+1)
    m = /^([A-Za-z]{3})-(\d{4})$/.exec(n);
    if (m) {
      const next = `${m[1]}-${Number(m[2]) + 1}`;
      if (monthContracts.some((c) => c.name === next)) return { name: next, label: "Month anno succ." };
      return { name: null, label: null };
    }
    return { name: null, label: null };
  }, [chartOverlay, primaryContractName, calContracts, quarterContracts, seasonContracts, monthContracts]);
  const overlayContractName = overlayInfo.name;
  const overlayCheckboxLabel = useMemo(() => {
    const n = primaryContractName || "";
    const cl = isCO2 ? "MidDec" : "CAL";
    if (!n || /^(Cal|MidDec)-/i.test(n)) return `Confronto ${cl} N+2`;
    if (/^Q[1-4]-/i.test(n)) return "Confronto Q anno succ.";
    if (/^(Sum|Win)-/i.test(n)) return "Confronto Season anno succ.";
    if (/^[A-Za-z]{3}-\d{4}$/.test(n)) return "Confronto Month anno succ.";
    return "Confronto anno succ.";
  }, [primaryContractName, isCO2]);
  // Se il contratto primario permette un confronto anno-su-anno
  const canOverlay = useMemo(() => {
    const n = primaryContractName || "";
    if (!n) return false;
    if (/^(Cal|MidDec)-/i.test(n)) return calContracts.length > 1;
    if (/^Q[1-4]-/i.test(n)) return quarterContracts.length > 0;
    if (/^(Sum|Win)-/i.test(n)) return seasonContracts.length > 0;
    if (/^[A-Za-z]{3}-\d{4}$/.test(n)) return monthContracts.length > 0;
    return false;
  }, [primaryContractName, calContracts, quarterContracts, seasonContracts, monthContracts]);

  // Persistenza scelte grafico in localStorage (per sheet)
  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({
        chartMode, chartDays, chartDateFrom, chartDateTo, chartContract, chartOverlay,
      }));
    } catch { /* localStorage disabilitato */ }
  }, [PREFS_KEY, chartMode, chartDays, chartDateFrom, chartDateTo, chartContract, chartOverlay]);

  const rangeValid = chartMode === "range" && chartDateFrom && chartDateTo && chartDateFrom <= chartDateTo;

  // Carica history del contratto primario + overlay opzionale
  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      setChartLoading(true);
      try {
        const baseParams = { source_sheet: sheet };
        if (chartMode === "range" && rangeValid) {
          baseParams.date_from = chartDateFrom;
          baseParams.date_to = chartDateTo;
        } else {
          baseParams.days = chartDays;
        }
        const params = { ...baseParams };
        if (chartContract) params.contract_name = chartContract;
        const ch = await api.get("/prices/contract-history", { params });
        if (cancelled) return;
        setContractHistory(ch.data);
        if (overlayContractName) {
          try {
            const ovr = await api.get("/prices/contract-history", { params: { ...baseParams, contract_name: overlayContractName } });
            if (!cancelled) setOverlayHistory(ovr.data);
          } catch { if (!cancelled) setOverlayHistory({ contract_name: null, points: [] }); }
        } else {
          setOverlayHistory({ contract_name: null, points: [] });
        }
      } catch {
        if (!cancelled) {
          setContractHistory({ contract_name: null, points: [], first_date: null, last_date: null });
          setOverlayHistory({ contract_name: null, points: [] });
        }
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [sheet, chartMode, chartDays, chartDateFrom, chartDateTo, rangeValid, chartContract, overlayContractName]);

  const lastPublishedAt = useMemo(() => {
    let max = "";
    for (const p of prices) {
      const ts = p?.imported_at || p?.update_time;
      if (ts && ts > max) max = ts;
    }
    return max || null;
  }, [prices]);

  const years = useMemo(() => {
    const s = new Set(prices.map((p) => p.period_year).filter(Boolean));
    return Array.from(s).sort();
  }, [prices]);

  const MONTH_MAP = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
  const parseMonthContract = (name) => {
    const m = /^([A-Za-z]{3})-(\d{4})$/.exec(name || "");
    if (!m) return null;
    const mi = MONTH_MAP[m[1]];
    if (mi == null) return null;
    return new Date(Number(m[2]), mi, 1).getTime();
  };

  const PERIOD_ORDER = { Spot: 0, Month: 1, Quarter: 2, Season: 3, MidDec: 3.5, Calendar: 4 };
  const contractSortKey = (name) => {
    if (!name) return { t: 0, s: "" };
    // Spot / Futures Today → sempre in cima
    if (/^(futures today|spot)$/i.test(name)) return { t: -1, s: name };
    // Prova a parsare "MMM-YYYY" (Month contracts)
    const t = parseMonthContract(name);
    if (t != null) return { t, s: name };
    // MidDec-YYYY
    const md = /^MidDec-(\d{4})$/i.exec(name);
    if (md) return { t: new Date(Number(md[1]), 11, 1).getTime(), s: name };
    // Prova "Q1-2026" / "Q2-2026" / "Sum-25" / "Win-25/26" / "Cal-2026"
    const q = /^Q([1-4])-(\d{4})$/.exec(name);
    if (q) return { t: new Date(Number(q[2]), (Number(q[1]) - 1) * 3, 1).getTime(), s: name };
    const seasonSum = /^Sum-(\d{2})$/.exec(name);
    if (seasonSum) return { t: new Date(2000 + Number(seasonSum[1]), 3, 1).getTime(), s: name };
    const seasonWin = /^Win-(\d{2})(?:\/\d{2})?$/.exec(name);
    if (seasonWin) return { t: new Date(2000 + Number(seasonWin[1]), 9, 1).getTime(), s: name };
    const cal = /^Cal-(\d{4})$/.exec(name);
    if (cal) return { t: new Date(Number(cal[1]), 0, 1).getTime(), s: name };
    return { t: Number.MAX_SAFE_INTEGER, s: name };
  };
  const compareValues = (av, bv, dir) => {
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return dir === "asc" ? -1 : 1;
    if (av > bv) return dir === "asc" ? 1 : -1;
    return 0;
  };

  const filtered = useMemo(() => {
    let arr = prices;
    if (periodType) arr = arr.filter((p) => p.period_type === periodType);
    if (year) arr = arr.filter((p) => String(p.period_year) === year);
    const dir = sortDir;
    return arr.slice().sort((a, b) => {
      if (sortKey === "contract_name") {
        const ka = contractSortKey(a.contract_name);
        const kb = contractSortKey(b.contract_name);
        const byT = compareValues(ka.t, kb.t, dir);
        return byT !== 0 ? byT : compareValues(ka.s, kb.s, dir);
      }
      if (sortKey === "period_type") {
        return compareValues(PERIOD_ORDER[a.period_type] || 99, PERIOD_ORDER[b.period_type] || 99, dir);
      }
      return compareValues(a[sortKey], b[sortKey], dir);
    });
  }, [prices, periodType, year, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const sortIcon = (k) => (sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  const now = Date.now();

  // Nuovo grafico: evoluzione mensile del contratto CAL N+1 (o MidDec N+1 per CO2)
  const yDigits = isCO2 ? 2 : (useSmc ? 3 : 2);
  const deltaDigits = isCO2 ? 2 : (useSmc ? 3 : 2);
  const fmtIt = (v, d = yDigits) => (v == null ? "—" : Number(v).toFixed(d).replace(".", ","));
  const fmtDayIt = (d) => {
    if (!d) return "";
    const parts = String(d).slice(0, 10).split("-");
    return parts.length === 3 ? `${parts[2]}/${parts[1]}` : d;
  };

  const historyPoints = (contractHistory.points || []).map((p) => ({
    date: p.date,
    close: conv(p.close != null ? p.close : p.last),
  })).filter((p) => p.close != null);
  const overlayByDate = useMemo(() => {
    const m = new Map();
    for (const p of (overlayHistory.points || [])) {
      const v = conv(p.close != null ? p.close : p.last);
      if (v != null) m.set(p.date, v);
    }
    return m;
  }, [overlayHistory, useSmc]);
  const historyChart = historyPoints.map((p, i) => ({
    date: p.date,
    close: p.close,
    overlay: overlayByDate.get(p.date) ?? null,
    delta: i > 0 && historyPoints[i - 1].close != null ? +(p.close - historyPoints[i - 1].close) : null,
  }));
  const hasHistory = historyChart.length > 0;
  const historyContract = contractHistory.contract_name;
  const overlayContract = overlayHistory.contract_name;
  const hasOverlay = historyChart.some((d) => d.overlay != null);
  const historyDeltaData = historyChart.filter((d) => d.delta != null);
  const hasHistoryDelta = historyDeltaData.length > 0;

  // Zoom automatico asse Y: dataMin/dataMax con padding ~15% per rendere leggibili piccole variazioni
  const yValues = historyChart.flatMap((d) => [d.close, d.overlay]).filter((v) => v != null);
  let yDomain = ["auto", "auto"];
  if (yValues.length > 0) {
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    const range = yMax - yMin;
    const pad = range > 0 ? range * 0.15 : Math.max(yMax * 0.02, 0.1);
    yDomain = [+(yMin - pad).toFixed(4), +(yMax + pad).toFixed(4)];
  }
  // Simmetrico per delta (rosso = aumento, verde = calo)
  const deltaAbsMax = hasHistoryDelta ? Math.max(...historyDeltaData.map((d) => Math.abs(d.delta))) : 0;
  const deltaDomain = deltaAbsMax > 0 ? [-deltaAbsMax * 1.2, deltaAbsMax * 1.2] : [-1, 1];
  const totalHistoryChange = (hasHistory && historyChart.length > 1)
    ? historyChart[historyChart.length - 1].close - historyChart[0].close
    : null;
  const latestUpd = prices.map((p) => p.update_time).filter(Boolean).sort().pop();

  const cals = prices
    .filter((p) => (isCO2 ? p.period_type === "MidDec" : p.period_type === "Calendar") && p.ask != null && p.period_year)
    .sort((a, b) => Number(a.period_year) - Number(b.period_year));
  const nextCal = cals[0];
  const nextCal2 = cals[1];
  const lastVals = prices.filter((p) => p.last_price != null).map((p) => p.last_price);

  const calLabel = isCO2 ? "MidDec" : "CAL";
  const summary = [
    { label: nextCal ? `Forward ASK ${calLabel} ${nextCal.period_year}` : `${calLabel} N+1`, value: nextCal ? fmtNum(conv(nextCal.ask), useSmc ? 3 : 2) : "—" },
    { label: nextCal2 ? `Forward ASK ${calLabel} ${nextCal2.period_year}` : `${calLabel} N+2`, value: nextCal2 ? fmtNum(conv(nextCal2.ask), useSmc ? 3 : 2) : "—" },
    { label: `Last min (${unitLabel})`, value: lastVals.length ? fmtNum(conv(Math.min(...lastVals)), useSmc ? 3 : 2) : "—" },
    { label: "Ultimo update", value: fmtDateTime(latestUpd), mono: false },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500 mt-1">Curva forward — foglio sorgente: <span className="font-mono">{sheet}</span> · Unità: <span className="font-semibold">{unitLabel}</span>{isGas && " (per gas: 1 €/MWh = 1,05833 c€/Smc)"}{isCO2 && " (contratti EUA — futures europei sulle emissioni)"}</p>
        <div className="mt-2">
          <LastUpdateChip ts={lastPublishedAt} autoRefreshing={refreshing} testid={`forward-${sheet.toLowerCase()}-last-update`} />
        </div>
        {isGas && (
          <div className="mt-3 inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="unit-toggle">
            <button onClick={() => setUnitGas("mwh")} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${!useSmc?"bg-white shadow text-blue-600":"text-slate-500"}`} data-testid="unit-mwh">€/MWh</button>
            <button onClick={() => setUnitGas("smc")} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${useSmc?"bg-white shadow text-blue-600":"text-slate-500"}`} data-testid="unit-smc">c€/Smc</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {summary.map((s, i) => (
          <div key={i} className="card-elevated p-4">
            <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">{s.label}</div>
            <div className={`mt-1 font-display font-bold text-xl text-slate-900 ${s.mono !== false ? "font-mono-nums" : ""}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {hasHistory && (
        <div className="card-elevated p-5" data-testid="forward-history-chart">
          {/* Toolbar: selettore contratto + periodo + overlay */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Contratto</label>
              <select
                value={chartContract}
                onChange={(e) => setChartContract(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
                data-testid="chart-contract-select"
              >
                <option value="">{calLabel} N+1{calContracts[0] ? ` (${calContracts[0].name})` : ""} — auto</option>
                {monthContracts.length > 0 && (
                  <optgroup label="Contratti Month">
                    {monthContracts.map((c) => (
                      <option key={c.name} value={c.name}>Month · {c.name}</option>
                    ))}
                  </optgroup>
                )}
                {quarterContracts.length > 0 && (
                  <optgroup label="Contratti Quarter">
                    {quarterContracts.map((c) => (
                      <option key={c.name} value={c.name}>Quarter · {c.name}</option>
                    ))}
                  </optgroup>
                )}
                {seasonContracts.length > 0 && (
                  <optgroup label="Contratti Season">
                    {seasonContracts.map((c) => (
                      <option key={c.name} value={c.name}>Season · {c.name}</option>
                    ))}
                  </optgroup>
                )}
                {calContracts.length > 0 && (
                  <optgroup label={isCO2 ? "Contratti MidDec" : "Contratti Calendar"}>
                    {calContracts.map((c, i) => (
                      <option key={c.name} value={c.name}>{calLabel} N+{i + 1} · {c.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="chart-mode-toggle">
                <button
                  onClick={() => setChartMode("preset")}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-semibold ${chartMode === "preset" ? "bg-white shadow text-blue-600" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid="chart-mode-preset"
                >Preset</button>
                <button
                  onClick={() => setChartMode("range")}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-semibold ${chartMode === "range" ? "bg-white shadow text-blue-600" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid="chart-mode-range"
                >Range</button>
              </div>
              {chartMode === "preset" ? (
                <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="chart-period-toggle">
                  {[
                    { d: 30, l: "30gg" },
                    { d: 90, l: "90gg" },
                    { d: 180, l: "6M" },
                    { d: 365, l: "1 anno" },
                  ].map((opt) => (
                    <button
                      key={opt.d}
                      onClick={() => setChartDays(opt.d)}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold ${chartDays === opt.d ? "bg-white shadow text-blue-600" : "text-slate-500 hover:text-slate-700"}`}
                      data-testid={`chart-period-${opt.d}`}
                    >{opt.l}</button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2" data-testid="chart-range-pickers">
                  <label className="text-[11px] text-slate-500 font-semibold">Dal</label>
                  <input
                    type="date"
                    value={chartDateFrom}
                    onChange={(e) => setChartDateFrom(e.target.value)}
                    max={chartDateTo || undefined}
                    className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-500"
                    data-testid="chart-date-from"
                  />
                  <label className="text-[11px] text-slate-500 font-semibold">Al</label>
                  <input
                    type="date"
                    value={chartDateTo}
                    onChange={(e) => setChartDateTo(e.target.value)}
                    min={chartDateFrom || undefined}
                    className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-500"
                    data-testid="chart-date-to"
                  />
                </div>
              )}
            </div>
            {canOverlay && (
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer" data-testid="chart-overlay-toggle">
                <input
                  type="checkbox"
                  checked={chartOverlay}
                  onChange={(e) => setChartOverlay(e.target.checked)}
                  className="rounded border-slate-300"
                />
                {overlayCheckboxLabel}
              </label>
            )}
          </div>

          <div className="font-display font-semibold text-slate-900 mb-2 flex items-center justify-between flex-wrap gap-1">
            <span>Andamento {(() => {
              const n = historyContract || "";
              if (/^Cal-/i.test(n) || /^MidDec-/i.test(n)) return calLabel + " " + n;
              if (/^Q[1-4]-/i.test(n)) return "Quarter " + n;
              if (/^(Sum|Win)-/i.test(n)) return "Season " + n;
              if (/^[A-Za-z]{3}-\d{4}$/.test(n)) return "Month " + n;
              return n;
            })()}{hasOverlay ? ` vs ${overlayContract}` : ""} ({unitLabel})</span>
            <div className="flex items-center gap-3 text-[11px] font-normal text-slate-500">
              {chartLoading && <span className="text-blue-600">Caricamento…</span>}
              {contractHistory.first_date && contractHistory.last_date && (
                <span>{fmtDayIt(contractHistory.first_date)} → {fmtDayIt(contractHistory.last_date)}</span>
              )}
              {totalHistoryChange != null && (
                <span className={`font-semibold ${totalHistoryChange > 0 ? "text-rose-600" : totalHistoryChange < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                  {totalHistoryChange > 0 ? "+" : ""}{fmtIt(totalHistoryChange)} {unitLabel}
                </span>
              )}
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historyChart} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="strokeGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#0B4DFF" />
                    <stop offset="100%" stopColor="#7B2CFF" />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#EEF0F5" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "#6B7280" }}
                  tickFormatter={fmtDayIt}
                  minTickGap={20}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#6B7280" }}
                  width={55}
                  domain={yDomain}
                  tickFormatter={(v) => fmtIt(v)}
                  allowDecimals
                />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
                  formatter={(v) => v == null ? "—" : `${fmtIt(v)} ${unitLabel}`}
                  labelFormatter={(l) => `Data: ${fmtDayIt(l)}`}
                />
                {hasOverlay && <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 11 }} />}
                {hasOverlay && <Line type="monotone" dataKey="overlay" name={overlayContract} stroke="#9CA3AF" strokeWidth={1.5} strokeDasharray="5 4" dot={{ r: 2, fill: "#9CA3AF" }} activeDot={{ r: 4 }} connectNulls />}
                <Line type="monotone" dataKey="close" name={historyContract || "Close"} stroke="url(#strokeGrad)" strokeWidth={2.5} dot={{ r: 3, fill: "#0B4DFF" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {hasHistoryDelta && (
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="forward-delta-chart">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                <div className="text-xs font-semibold text-slate-700">Δ giornaliero ({unitLabel})</div>
                <div className="flex items-center gap-3 text-[10px] text-slate-500">
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Calo prezzo</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-500" /> Aumento prezzo</span>
                </div>
              </div>
              <div className="h-28">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={historyDeltaData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#EEF0F5" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7280" }} tickFormatter={fmtDayIt} minTickGap={20} />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#6B7280" }}
                      width={55}
                      domain={deltaDomain}
                      tickFormatter={(v) => (v > 0 ? "+" : "") + fmtIt(v, deltaDigits)}
                      allowDecimals
                    />
                    <ReferenceLine y={0} stroke="#94A3B8" strokeWidth={1} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
                      formatter={(v) => v == null ? "—" : `${v > 0 ? "+" : ""}${fmtIt(v, deltaDigits)} ${unitLabel}`}
                      labelFormatter={(l) => `Data: ${fmtDayIt(l)}`}
                    />
                    <Bar dataKey="delta" radius={[3, 3, 0, 0]}>
                      {historyDeltaData.map((d, i) => (
                        <Cell key={i} fill={d.delta > 0 ? "#EF4444" : d.delta < 0 ? "#10B981" : "#94A3B8"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card-elevated p-4 flex flex-wrap gap-2">
        <select value={periodType} onChange={(e) => setPeriodType(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500" data-testid="forward-filter-period">
          <option value="">Periodo: Tutti</option>
          {isCO2 && <option value="Spot">Spot</option>}
          <option value="Month">Month</option>
          <option value="Quarter">Quarter</option>
          <option value="Season">Season</option>
          {isCO2 && <option value="MidDec">MidDec</option>}
          <option value="Calendar">Calendar</option>
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500" data-testid="forward-filter-year">
          <option value="">Anno: Tutti</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table w-full min-w-[800px]">
            <thead>
              <tr>
                {[
                  { k: "contract_name", l: "Contract" },
                  { k: "period_type", l: "Periodo" },
                  { k: "previous_close", l: "Close" },
                  { k: "ask", l: "Ask" },
                  { k: "bid", l: "Bid" },
                  { k: "last_price", l: "Last" },
                  { k: "change_value", l: "Change" },
                  { k: "update_time", l: "UpdateTime" },
                ].map(({ k, l }) => (
                  <th
                    key={k}
                    onClick={() => toggleSort(k)}
                    className="cursor-pointer hover:text-blue-600 select-none"
                    data-testid={`fwd-sort-${k}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {l}
                      {k === "change_value" && (
                        <UiTooltipProvider delayDuration={100}>
                          <UiTooltip>
                            <UiTooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <span className="text-slate-400 hover:text-blue-600 cursor-help" data-testid="fwd-change-tooltip">
                                <Info size={12} />
                              </span>
                            </UiTooltipTrigger>
                            <UiTooltipContent side="top" className="max-w-xs text-xs font-normal">
                              {CHANGE_TOOLTIP_TEXT}
                            </UiTooltipContent>
                          </UiTooltip>
                        </UiTooltipProvider>
                      )}
                      {sortIcon(k)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center text-slate-400 py-8">Caricamento…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">Nessun dato</td></tr>}
              {filtered.map((p) => {
                const dig = useSmc ? 4 : 3;
                return (
                <tr key={p.id}>
                  <td className="!font-sans !font-semibold">{p.contract_name}</td>
                  <td>{p.period_type}</td>
                  <td>{fmtNum(conv(p.previous_close), dig)}</td>
                  <td className="font-semibold">{fmtNum(conv(p.ask), dig)}</td>
                  <td>{fmtNum(conv(p.bid), dig)}</td>
                  <td className="font-semibold">{fmtNum(conv(p.last_price), dig)}</td>
                  <td>
                    {p.change_value != null && (
                      <span className={p.change_value > 0 ? "badge-neg" : p.change_value < 0 ? "badge-pos" : "badge-neutral"}>
                        {p.change_value > 0 ? <TrendingUp size={11} /> : p.change_value < 0 ? <TrendingDown size={11} /> : null}
                        {fmtChange(conv(p.change_value))}
                      </span>
                    )}
                  </td>
                  <td className="text-[11px] text-slate-500">{fmtDateTime(p.update_time)}</td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
